/**
 * /guides/how-to-organize-wedding-rsvps
 *
 * "How to Organize Wedding RSVPs Without Losing Track of Guests" — the fifth
 * production guide, and the second in the RSVP cluster. Where the fourth guide
 * answers WHEN guests should reply, this one answers what happens to the
 * answers once they start arriving.
 *
 * Its argument is that the hard part of RSVPs is record-keeping, not asking:
 * one central guest record, a current status on every invitation, and people
 * counted as people rather than as accepted invitations. Everything else in the
 * article is downstream of that one idea, and the chain it keeps explicit —
 * guest list to RSVP to confirmed guests to final count to seating — is the
 * spine of the piece. Do not collapse a link out of it in a later edit.
 *
 * Two hedges are load-bearing and must survive editing. There is no guest count
 * at which a spreadsheet stops working, and there is no universal review
 * cadence; the article says so in both places, because a reader whose hand-kept
 * list works perfectly well is not doing it wrong. The same applies to
 * reminders: the advice to follow up is addressed to the reader, never
 * described as something a product does on their behalf.
 *
 * ── The claims this guide is allowed to make ──────────────────────────────
 * Four, all in the closing section, and nothing beyond them:
 *
 *   - "Agapaé's RSVP tools are available on the Heirloom plan."  (the note)
 *     True at `api/constants/planFeatures.mjs`: RSVP is part of
 *     `invitationProduct`, which is `pro` (Heirloom) only.
 *   - couples manage the guests they have invited  (GuestSchema)
 *   - couples review the responses, with the people in each party
 *     (PartyMemberSchema — an invitation carries members with roles)
 *   - attendance is recorded per moment where an event has more than one
 *     (AttendanceResponseSchema — a member x section matrix)
 *
 * And the things this guide must never say, because none of them exist:
 * automatic reminders, automated follow-ups, automatic vendor notifications,
 * automatic final guest-count submission, automatic seating generation,
 * personalized RSVP emails, a custom RSVP question builder, or guest-entered
 * dietary preferences. Dietary requirements in Agapaé are couple-entered, which
 * is why the article's one mention of them is advice about where a detail
 * should live and is never attributed to the product.
 *
 * Nothing here may imply that RSVP records and photo-gallery records are
 * identity-linked; they are not, and the guide never mentions the gallery.
 *
 * ── Three structural notes ────────────────────────────────────────────────
 * The source article numbers its sections 1 to 14. The numbers are dropped, as
 * the third guide dropped its ordinal words: the headings then read as an
 * argument rather than a checklist, and the one place the order genuinely
 * carries meaning — the workflow — is a `steps` list, which numbers itself.
 *
 * Only two internal links, each placed where a reader actually needs it: the
 * seating guide at the point the article hands off to seating, and the product
 * page in the closing section. `related.guides` is deliberately empty for the
 * same reason the four earlier guides left it empty — a second link to the same
 * guide under that heading would be a link built for a crawler.
 *
 * No copy in this file may contain "&", "<" or ">". The generator escapes them
 * correctly, but `guides.seo.test.mjs` compares against tag-stripped HTML
 * without decoding entities, so a string containing one is reported missing
 * from a document that in fact carries it.
 */

const howToOrganizeWeddingRsvpsEn = {
  slug: "how-to-organize-wedding-rsvps",

  title: "How to Organize Wedding RSVPs Without Losing Track of Guests",
  metaTitle:
    "How to Organize Wedding RSVPs Without Losing Track of Guests | Agapaé",
  metaDescription:
    "Learn how to organize wedding RSVPs, track guest responses, manage plus-ones and follow-ups, and turn replies into a reliable final guest list.",

  eyebrow: "Wedding RSVP",

  hero: {
    src: "/assets/images/ceremony.jpeg",
    // Describes the photograph, not the keyword: a black and white outdoor
    // ceremony, the couple facing each other between two tall floral
    // arrangements, part of the wedding party at the left edge, and the raised
    // hands of guests out of focus across the foreground. The guests are what
    // make it the right picture for this article, and they are genuinely in it
    // — describing anything that is not would be a lie told to exactly the
    // readers who cannot see it.
    alt: "A black and white photograph of a bride and groom facing each other at an outdoor ceremony between two tall floral arrangements, with the raised hands of guests blurred across the foreground",
    width: 1280,
    height: 714,
  },

  // Absent until the guide is genuinely published. Adding one means adding the
  // other, and `dateModified` may not precede `datePublished`.
  // datePublished: "YYYY-MM-DD",
  // dateModified: "YYYY-MM-DD",

  intro: [
    `Asking your guests to reply is the easy part of a wedding RSVP. Keeping the answers organized once they start arriving is the part that quietly becomes difficult.`,
    `Responses rarely come through one channel. Some guests use the RSVP form on your invitation; others reply by text, by email, by phone, on a paper card, or through a family member.`,
    `The trouble begins when those answers live in different places and you no longer have one reliable way to know who was invited, who has replied, who is attending, how many people are coming, and who still needs a follow-up.`,
    `This guide is about how to organize wedding RSVPs so the information stays usable: one central guest record, a clear status on every invitation, and a reliable way to turn responses into a confirmed guest list.`,
  ],

  sections: [
    {
      heading: "How to Organize Wedding RSVPs",
      content: [
        `Keep one central guest record, and give every invited guest or party a current RSVP status inside it.`,
        `That record can take almost any form:`,
        {
          type: "list",
          items: [
            `a spreadsheet`,
            `a shared document both partners can edit`,
            `a dedicated wedding RSVP system`,
            `any other structured guest list you will genuinely keep current`,
          ],
        },
        `The format matters far less than the rule that goes with it: there is one list, and it is the one that is right.`,
        `Competing lists are what break RSVP tracking. One partner keeps a spreadsheet, the other answers messages on their phone, a parent collects replies at a family lunch, and nobody can say how many people are coming.`,
      ],
    },
    {
      heading: "Start With One Master Guest List",
      content: [
        `RSVP tracking begins with the invitation list, not with the first response.`,
        `Before any replies arrive, every invited guest or party should already exist as a record. Otherwise the list grows one answer at a time, and the people who never respond never appear at all.`,
        `A useful record usually holds:`,
        {
          type: "list",
          items: [
            `the guest name`,
            `the household or party the invitation covers`,
            `whether the invitation has been sent`,
            `the current RSVP status`,
            `how many people are attending`,
            `companion or plus-one details, where they apply`,
            `notes that affect planning`,
            `where the follow-up stands`,
          ],
        },
        `A wedding guest list is not a database of everyone you know. It should hold what you need in order to make decisions, and little else.`,
      ],
    },
    {
      heading: "Give Every Invitation a Clear RSVP Status",
      content: [
        `The simplest status model that works has three states: pending, attending, declined.`,
        `Every invitation starts as pending, which means only that no confirmed response has been recorded yet.`,
        `The purpose of a status is visibility: unanswered invitations should be obvious at a glance rather than discovered late.`,
        `Memory works for the first dozen replies. After that, searching an inbox for an answer someone gave six weeks ago is not a system.`,
        `Some couples add a fourth state for guests who have replied without deciding. Keep any extra states few and clearly defined, or the status stops meaning anything.`,
      ],
    },
    {
      heading: `Track People, Not Just "Yes" and "No"`,
      content: [
        `One invitation is not one guest, and this is where RSVP tracking most often goes wrong.`,
        `Maria and Nikos receive a single invitation. Maria replies for both of them, so one acceptance represents two people at the reception. A family invitation might represent four.`,
        `A tracker that records only yes and no can tell you how many invitations were accepted. It cannot tell you how many chairs to put out.`,
        `So keep three things distinguishable:`,
        {
          type: "list",
          items: [
            `the invited party, which is what you sent`,
            `the individual guests that party covers`,
            `the number of people actually attending`,
          ],
        },
        `Catering, table capacity and seating all work in people, not in invitations.`,
      ],
    },
    {
      heading: "Keep Plus-Ones and Companions Clear",
      content: [
        `A yes does not always tell you enough.`,
        `If an invitation includes a companion, you need to know whether the guest is bringing someone and, where it matters, who that person is.`,
        `How much detail you need depends on the wedding. Some couples only need the number; others want the name, for place cards, seating and introductions.`,
        `Neither is more correct. Collect what your wedding actually uses.`,
        `The principle underneath both is the same: resolve the people behind the headcount.`,
        ],
    },
    {
      heading: "Separate Confirmed, Declined and Still Waiting",
      content: [
        `These three groups do different work, so they should never blur into each other.`,
        {
          type: "list",
          items: [
            `Confirmed guests contribute to your working guest count.`,
            `Declined guests should stop being counted as attending, but should not disappear.`,
            `Guests you are still waiting on belong on the follow-up list, and in neither total.`,
          ],
        },
        `Deleting a declined guest is tempting and usually a mistake: you lose the record that they were invited and that they answered.`,
        `While the three groups stay distinguishable, your guest list is a planning document rather than a record of messages.`,
      ],
    },
    {
      heading: "Keep Important RSVP Details With the Response",
      content: [
        `Some answers arrive with more than a yes attached. Depending on the wedding, a response may carry:`,
        {
          type: "list",
          items: [
            `a meal choice, if your wedding asks for one`,
            `dietary requirements or notes, where you collect them`,
            `companion information`,
            `attendance for a specific moment, when the invitation covers more than one`,
          ],
        },
        `No wedding needs all of this, and collecting what you will never use only makes the RSVP harder.`,
        `What matters is where a detail ends up: attached to the guest it belongs to, in the same record that holds their status.`,
        `A dietary note left in a message thread is one you will be hunting for in the week you have least time to spare.`,
      ],
    },
    {
      heading: "Create a Simple Follow-Up List",
      content: [
        `Guests who have not replied should become a visible list rather than something you try to remember.`,
        `A practical follow-up list distinguishes a few states:`,
        {
          type: "list",
          items: [
            `not yet responded`,
            `reminder needed`,
            `contacted directly`,
            `response received`,
          ],
        },
        `Some couples send reminders in a batch, some ask a parent to make a few calls, some prefer to speak to everyone themselves. None of it has to be automated to be reliable.`,
        `What the list gives you is the part that is hard to hold in your head: who needs attention, and why.`,
        `It also prevents one guest being asked three times while another is never asked at all.`,
      ],
    },
    {
      heading: "When a Guest Replies Outside the RSVP System",
      content: [
        `However carefully you set up your RSVP, some answers will arrive another way:`,
        {
          type: "list",
          items: [
            `a text message`,
            `a phone call`,
            `an email reply`,
            `a family member passing the answer along`,
            `a conversation at another event`,
          ],
        },
        `This is normal, and worth expecting: guests reply through whatever is easiest for them.`,
        `The failure is not the channel. It is the answer that never makes it into the master record.`,
        `Update the record as soon as you can, or gather the loose answers in a regular review. Either works, as long as the answer lands somewhere permanent.`,
        `Once it is recorded, that guest should no longer appear as pending simply because they did not use the form. Otherwise you spend the last month chasing people who already replied.`,
      ],
    },
    {
      heading: "Review the Guest List Regularly",
      content: [
        `A tracker is only useful while it is current, and RSVP information goes stale quietly.`,
        `A short review does most of the work:`,
        {
          type: "list",
          items: [
            `record any new responses`,
            `check who is still pending`,
            `verify party and companion details`,
            `look at the current attending count`,
            `resolve anything ambiguous while you still remember the conversation`,
          ],
        },
        `How often depends on your wedding and where you are in the timeline: it matters far more in the weeks around the RSVP deadline than months earlier.`,
        `It does not need to take long, only to happen often enough that the number on your guest list is one you would give a caterer.`,
      ],
    },
    {
      heading: "Keep the RSVP Deadline Separate From the Final Guest Count",
      content: [
        `These two dates are related, and they are not the same thing.`,
        `The RSVP deadline is the date you ask your guests to respond by. The final guest count is the number you give your venue, caterer or other vendors, on the deadline their own planning sets.`,
        `Treating them as one date removes the space where the work in this guide happens: following up, resolving unclear answers, confirming party sizes.`,
        `Think of the RSVP deadline as the point where your final guest count starts being assembled, rather than the point where it is finished.`,
      ],
    },
    {
      heading: "Turn Your Confirmed Guest List Into the Next Step",
      content: [
        `Once the RSVP picture is reliable, the guest list starts doing real work. A confirmed list of attending guests supports:`,
        {
          type: "list",
          items: [
            `your final guest count`,
            `attendance for the ceremony, the reception, or whichever moments your invitation covers`,
            `catering and meal planning`,
            `your wedding seating plan`,
            `anything else that depends on knowing who is actually in the room`,
          ],
        },
        `None of it happens on its own; your vendors still need to be told.`,
        `Seating is where the quality of your RSVP records shows: every table depends on knowing which specific people are attending and which of them belong together.`,
        {
          type: "p",
          text: [
            `If that is your next step, `,
            {
              text: "How to Create a Wedding Seating Plan: From RSVP to Every Table",
              href: "/guides/wedding-seating-plan",
            },
            ` picks up where this guide ends.`,
          ],
        },
      ],
    },
    {
      heading: "Spreadsheet or RSVP Tool: Which Should You Use?",
      content: [
        `Both work, and the honest answer depends on your wedding rather than on the tool.`,
        `A spreadsheet is a genuinely good choice when:`,
        {
          type: "list",
          items: [
            `you are comfortable maintaining it by hand`,
            `the guest list is a size you can take in at a glance`,
            `responses arrive through only one or two channels`,
            `both partners can reliably keep the same file updated`,
          ],
        },
        `A dedicated RSVP system tends to help when:`,
        {
          type: "list",
          items: [
            `there are a lot of guests`,
            `responses arrive often, and from several directions`,
            `you need structured information rather than free text, such as who is in each party`,
            `keeping several manual lists in step has become a task of its own`,
          ],
        },
        `There is no guest count at which a spreadsheet stops working.`,
        `The question worth asking is simpler: is the system still reliable, and is it still easy to keep current?`,
      ],
    },
    {
      heading: "A Simple Wedding RSVP Tracking Workflow",
      content: [
        `Put together, the whole process is short enough to write down:`,
        {
          type: "steps",
          items: [
            `Start with the complete list of invited guests.`,
            `Record when invitations are sent.`,
            `Give every invitation a current RSVP status.`,
            `Record the actual people attending, not only the accepted invitations.`,
            `Resolve companion and plus-one details where they apply.`,
            `Keep relevant RSVP notes with the guest they belong to.`,
            `Review pending responses before and around the deadline.`,
            `Follow up with the guests who have not responded.`,
            `Reconcile the confirmed guest list.`,
            `Use the confirmed list for your final count and your seating plan.`,
          ],
        },
        `Each step protects the one after it: skip the fourth and your final count is a guess.`,
      ],
    },
    {
      // The source article closes with a separate Agapaé section, which is the
      // heading the `related` block below already renders as an H2. Two H2s
      // reading the same words is a defect, and all four earlier guides
      // resolved it the same way: the closing prose section is named for what
      // it does, and "Where this fits in Agapaé" stays with the related block.
      heading: "The Goal Is a Guest List You Can Trust",
      content: [
        `The best RSVP tracker is not the most complicated one.`,
        `It is the one that lets you answer, without opening anything else:`,
        {
          type: "list",
          items: [
            `who was invited`,
            `who has replied`,
            `who is attending`,
            `how many people are coming`,
            `who still needs attention`,
            `what is still unresolved`,
          ],
        },
        `If your spreadsheet answers all six, it is a good system. If it no longer does, that is worth fixing before the deadline.`,
        {
          // The only plan-availability claim in the guide, in the same place
          // and the same block type the four earlier guides put theirs.
          type: "note",
          text: `Agapaé's RSVP tools are available on the Heirloom plan.`,
        },
        `In Agapaé, invited guests and their responses live in the same place. Couples manage the guests they have invited and review the responses as they arrive, including the people in each party and, where an event has more than one moment, who is attending which.`,
        {
          type: "p",
          text: [
            `For the product-specific RSVP experience, see `,
            { text: "Wedding RSVP", href: "/wedding-rsvp" },
            `.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Frequently Asked Questions",

  // Visible copy, always expanded. `faqSchema` is deliberately not set, exactly
  // as on the four earlier guides: Google restricts FAQ rich results to
  // government and health sites, so the markup would buy nothing while
  // committing us to a second copy of every answer.
  faq: [
    {
      q: `What is the best way to keep track of wedding RSVPs?`,
      a: `Keep one central guest record in which every invitation has a current status, and update it wherever the response happens to arrive from. A spreadsheet and a dedicated RSVP system both do this well. What causes trouble is not the tool but keeping several partial lists at once.`,
    },
    {
      q: `What should a wedding RSVP tracker include?`,
      a: `Enough to make decisions with: the guest name, the party the invitation covers, whether it has been sent, the RSVP status, the number attending, companion details where they apply, any notes your wedding actually uses, and where the follow-up stands. Every extra field is one more thing to keep current.`,
    },
    {
      q: `How do you track guests who have not responded?`,
      a: `Keep them as a visible list rather than a memory, and note what has already been tried so that nobody is contacted twice. Before following up, check whether someone else in their household has answered, or whether their reply came by text or phone and was never recorded.`,
    },
    {
      q: `Should plus-ones be included in the RSVP guest list?`,
      a: `Yes. A plus-one is a person who attends, eats and needs a seat, so they belong in the count. Record which invitations include a companion, whether that companion is coming, and their name where your planning needs it, such as for place cards or seating.`,
    },
    {
      q: `What should you do after all the wedding RSVPs are in?`,
      a: `Reconcile the list before you use it. Confirm the attending count, resolve any answers that are still unclear, verify party and companion details, and settle the remaining non-responses one way or the other. The confirmed list is then what your final guest count and your seating plan are built from.`,
    },
  ],

  related: {
    heading: "Where this fits in Agapaé",

    // Deliberately empty, matching all four earlier guides. The seating guide
    // is already linked from the prose at the point a reader actually needs
    // it; a second link to it under this heading would be a link built for a
    // crawler rather than for the person reading.
    guides: [],

    product: [
      {
        href: "/wedding-rsvp",
        label: "Wedding RSVP",
        blurb:
          "See how RSVPs work in Agapaé, from the guests you invite to the responses you review before the rest of your planning. RSVP tools are available on the Heirloom plan.",
      },
    ],
  },
};

export default howToOrganizeWeddingRsvpsEn;
